import type { Action, ActionGroup } from "@/actions/action-types";
import { useRegisterCommandPaletteActions } from "@/actions/command-palette-registry";
import { useEnvironmentActions } from "@/actions/use-environment-actions";
import { EnvironmentContextBadge } from "@/components/command-palette/EnvironmentContextBadge";
import { SYSTEM_PORT_NAMES } from "@/components/command-palette/constants";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { useEnvironment } from "@/queries/environment-queries";
import { isManualTask, useListServices, useListTasks, useStartTask } from "@/queries/automations-queries";
import { canOpen } from "@/routes/environments/phase";
import { formatError } from "@/utils/errors";
import { NAV_STATE_DETAILS } from "@/routes/environments/details/environment-nav-state";
import { IconArrowRight } from "central-icons/IconArrowRight";
import { useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";

/** Static mapping from action group key to display heading and sort priority. */
export const GROUP_HEADINGS: Record<string, { heading: string; priority: number }> = {
    lifecycle: { heading: "Environment", priority: 0 },
    navigate: { heading: "Environment", priority: 1 },
    edit: { heading: "Environment", priority: 2 },
    info: { heading: "Environment", priority: 3 },
    danger: { heading: "Environment", priority: 4 },
    open: { heading: "IDEs", priority: 50 },
};

/**
 * Registers environment-specific command palette actions when mounted
 * on an environment details page. Cleans up on unmount.
 */
export function useRegisterEnvironmentCommands(environmentId: string): void {
    const { data: environment } = useEnvironment(environmentId);
    const navigate = useNavigate();
    const { toast } = useToast();

    // Core environment actions from the unified hook
    const envActions = useEnvironmentActions(environmentId);

    // Ports — derived from spec + status URLs
    const portActions = useMemo((): Action[] => {
        if (!environment || !canOpen(environment)) return [];
        const specPorts = environment.spec?.ports ?? [];
        const statusPorts = environment.status?.environmentUrls?.ports ?? [];

        return specPorts
            .filter((spec) => !SYSTEM_PORT_NAMES.has(spec.name))
            .map((spec) => {
                const status = statusPorts.find((p) => p.port === spec.port);
                const url = status?.url;
                return {
                    id: `port-${spec.port}`,
                    label: spec.name ? `${spec.name} (:${spec.port})` : `Port ${spec.port}`,
                    icon: <IconArrowRight />,
                    keywords: ["port", "open", String(spec.port)],
                    onSelect: () => {
                        if (url) window.open(url, "_blank", "noopener,noreferrer");
                    },
                    disabled: !url,
                };
            });
    }, [environment]);

    // Services
    const { data: services } = useListServices(environmentId);

    const serviceActions = useMemo((): Action[] => {
        if (!services || services.length === 0) return [];
        return services.map((service) => ({
            id: `service-${service.id}`,
            label: service.metadata?.name ?? service.id,
            icon: <IconArrowRight />,
            keywords: ["service"],
            onSelect: () => navigate(`/details/${environmentId}/services/${service.id}`, NAV_STATE_DETAILS),
        }));
    }, [services, environmentId, navigate]);

    // Tasks
    const { data: tasks } = useListTasks(environmentId);
    const startTask = useStartTask();

    const handleStartTask = useCallback(
        async (taskId: string) => {
            try {
                await startTask.mutateAsync(taskId);
            } catch (error) {
                toast({ title: "Failed to start task", description: formatError(error) });
            }
        },
        [startTask, toast],
    );

    const taskActions = useMemo((): Action[] => {
        if (!tasks || tasks.length === 0) return [];
        return tasks.map((task) => {
            const manual = isManualTask(task);
            return {
                id: `task-${task.id}`,
                label: task.metadata?.name ?? task.id,
                icon: <IconArrowRight />,
                keywords: ["task", "run"],
                onSelect: manual
                    ? () => void handleStartTask(task.id)
                    : () => navigate(`/details/${environmentId}/tasks/${task.id}`, NAV_STATE_DETAILS),
            };
        });
    }, [tasks, environmentId, navigate, handleStartTask]);

    // Troubleshooting
    const troubleshootingActions = useMemo((): Action[] => {
        if (!environmentId) return [];
        return [
            {
                id: "copy-logs",
                label: "Copy environment logs",
                keywords: ["troubleshoot", "debug", "logs", "copy"],
                onSelect: () => navigate(`/details/${environmentId}/logs`, NAV_STATE_DETAILS),
            },
            {
                id: "download-system-report",
                label: "Download system report",
                keywords: ["troubleshoot", "debug", "report", "download"],
                onSelect: () => navigate(`/details/${environmentId}/logs`, NAV_STATE_DETAILS),
            },
        ];
    }, [environmentId, navigate]);

    // Build groups — split envActions by their `group` field so IDEs
    // get their own section and navigate actions appear prominently.
    const groups = useMemo((): ActionGroup[] => {
        const result: ActionGroup[] = [];

        // Partition envActions by group, merging groups that share a heading
        if (envActions.length > 0) {
            const buckets = new Map<string, Action[]>();
            for (const action of envActions) {
                const key = action.group ?? "default";
                if (!buckets.has(key)) buckets.set(key, []);
                buckets.get(key)!.push(action);
            }

            // Merge buckets that share the same heading into a single ActionGroup
            const merged = new Map<string, { heading: string; priority: number; actions: Action[] }>();
            for (const [key, actions] of buckets) {
                const meta = GROUP_HEADINGS[key] ?? { heading: "Environment", priority: 3 };
                const existing = merged.get(meta.heading);
                if (existing) {
                    existing.actions.push(...actions);
                    existing.priority = Math.min(existing.priority, meta.priority);
                } else {
                    merged.set(meta.heading, { heading: meta.heading, priority: meta.priority, actions: [...actions] });
                }
            }

            for (const [, entry] of merged) {
                result.push({
                    id: `env-${entry.heading.toLowerCase().replace(/\s+/g, "-")}`,
                    heading: entry.heading,
                    actions: entry.actions,
                    priority: entry.priority,
                });
            }
        }

        if (portActions.length > 0) {
            result.push({
                id: "env-ports",
                heading: "Ports",
                actions: portActions,
                priority: 10,
            });
        }

        if (serviceActions.length > 0) {
            result.push({
                id: "env-services",
                heading: "Services",
                actions: serviceActions,
                priority: 20,
            });
        }

        if (taskActions.length > 0) {
            result.push({
                id: "env-tasks",
                heading: "Tasks",
                actions: taskActions,
                priority: 30,
            });
        }

        if (troubleshootingActions.length > 0) {
            result.push({
                id: "env-troubleshooting",
                heading: "Troubleshooting",
                actions: troubleshootingActions,
                priority: 40,
            });
        }

        return result;
    }, [envActions, portActions, serviceActions, taskActions, troubleshootingActions]);

    const contextBadge = useMemo(
        () => (environmentId ? <EnvironmentContextBadge environmentId={environmentId} /> : undefined),
        [environmentId],
    );

    useRegisterCommandPaletteActions(`env-${environmentId}`, groups, { contextBadge });
}
